import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Grid, Link, Typography } from '@mui/material';
import { AddCircleOutlineOutlined } from '@mui/icons-material';

import { FHButton, FHDurationTag, FHStopwatch } from '../../../../base';
import { useParentSupervisionEditable } from '../../../../../hooks';
import { parentSupervisionActions } from '../../../../../store';
import FHDurationTrialTime from '../../../../../components/base/fhDurationTrialTime';
import { FORTA_BLUE, FORTA_ERROR } from '../../../../../utils/colors';
import TimeTagStartIconComp from '../../../../svgs/TimeTagStartIcon';
import TimeTagEndIconComp from '../../../../svgs/TimeTagEndIcon';

import type { ITargetTrialDurationEvent } from '../../../../../utils/types';
import type { RootState } from '../../../../../store';

const COLLAPSED_EVENT_COUNT = 4;

interface IDurationTimeTrackerProps {
  dataLoading: boolean;
  trailId: number;
  durationEvents: ITargetTrialDurationEvent[];
}

function ParentSNDurationTargetRow({
  dataLoading,
  trailId,
  durationEvents,
}: IDurationTimeTrackerProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const [runTimer, setRunTimer] = useState<boolean>(false);
  const [collapseEvents, setCollapseEvents] = useState<boolean>(false);

  const { current: parentSupervision } = useSelector((state: RootState) => state.parentSupervision);
  const isEditable = useParentSupervisionEditable();

  const sessionDuration = parentSupervision?.expected_duration ?? 0;

  const totalTrialDuration = useMemo(
    () => durationEvents?.reduce((acc, duration) => acc + (duration?.duration ?? 0), 0),
    [durationEvents],
  );

  const availableTimeForDurations = sessionDuration - totalTrialDuration;

  const handleAddNewDurationEvent = async (duration: number) => {
    if (duration) {
      await dispatch(
        parentSupervisionActions.createDurationEvent({
          getAccessTokenSilently,
          parentSupervisionId: parentSupervision?.id,
          trailId,
          data: {
            duration,
          },
        }),
      ).unwrap();
      setCollapseEvents(false);
    }
  };

  const handleUpdateDurationEvent = async (durationEvent?: ITargetTrialDurationEvent) => {
    try {
      if (durationEvent?.id) {
        await dispatch(
          parentSupervisionActions.updateDurationEvent({
            getAccessTokenSilently,
            parentSupervisionId: parentSupervision?.id,
            trailId,
            durationEventId: durationEvent.id,
            data: {
              duration: durationEvent.duration,
            },
          }),
        ).unwrap();
      }
      return true;
    } catch {
      return false;
    }
  };
  const handleDeleteDurationEvent = (durationEventId: number) => {
    dispatch(
      parentSupervisionActions.deleteDurationEvent({
        getAccessTokenSilently,
        parentSupervisionId: parentSupervision?.id,
        trailId,
        durationEventId,
      }),
    );
  };

  const durationEventsToDisplay = collapseEvents ? durationEvents?.slice(0, 4) : durationEvents;

  return (
    <Box
      data-testid="session-duration-tracker-container"
      sx={{
        mt: 1,
        ml: { sm: 2, xs: 0.2 },
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
        border: `1px solid ${FORTA_BLUE.light2}`,
        padding: '8px 16px',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          height: '100%',
          pb: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            alignItems: 'flex-start',
            pt: 1,
            pb: { xs: 2, sm: 0 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'top',
              width: '100%',
            }}
          >
            <Box sx={{ display: 'flex' }}>
              <Typography
                sx={{
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  textAlign: 'left',
                  mr: 1,
                }}
              >
                Duration tracker:
              </Typography>
              <Typography
                sx={{
                  borderRadius: '8px',
                  fontSize: '16px',
                  textAlign: 'left',
                  mr: 1,
                }}
              >
                Total duration:
              </Typography>
              <FHDurationTrialTime seconds={totalTrialDuration} />
            </Box>
            <Box sx={{ display: 'flex' }}>
              {isEditable && (
                <>
                  <FHButton
                    sx={{ mr: 1 }}
                    startIcon={
                      <Box sx={{ mr: 2 }}>
                        <TimeTagStartIconComp />
                      </Box>
                    }
                    disabled={
                      runTimer || !isEditable || availableTimeForDurations < 1 || dataLoading
                    }
                    onClick={() => {
                      setRunTimer(true);
                      setCollapseEvents(false);
                    }}
                  >
                    Start
                  </FHButton>
                  <FHButton
                    sx={{ mr: 1 }}
                    startIcon={
                      <Box sx={{ mr: 2 }}>
                        <TimeTagEndIconComp />
                      </Box>
                    }
                    disabled={
                      !runTimer || !isEditable || availableTimeForDurations < 1 || dataLoading
                    }
                    onClick={() => setRunTimer(false)}
                  >
                    Stop
                  </FHButton>
                  <FHStopwatch
                    startTimer={runTimer}
                    timerLimit={availableTimeForDurations}
                    onStop={(duration) => {
                      handleAddNewDurationEvent(duration);
                      setRunTimer(false);
                      setCollapseEvents(false);
                    }}
                  />{' '}
                </>
              )}
            </Box>
          </Box>
          {runTimer && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <Typography fontSize={12} color={FORTA_ERROR}>
                Please stay on this page while the timer runs to avoid data loss.
              </Typography>
            </Box>
          )}
          {durationEvents.length > COLLAPSED_EVENT_COUNT && (
            <Link
              onClick={() => {
                setCollapseEvents((prev) => !prev);
              }}
              sx={{
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                color: FORTA_BLUE.bright,
                '&:hover': {
                  color: FORTA_BLUE.primary,
                },
                marginBottom: '16px',
              }}
            >
              {collapseEvents ? 'see more' : 'see less'}
            </Link>
          )}
          <Grid
            xs={12}
            sm={12}
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'center',
              py: 2,
            }}
          >
            {/* Dynamic duration tags */}
            {durationEvents.length > 0 ? (
              durationEventsToDisplay?.map((durationEvent) => (
                <Box
                  key={durationEvent.id || null}
                  sx={{
                    width: 222,
                    minHeight: 80,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <FHDurationTag
                    id={durationEvent.id ?? 0}
                    duration={durationEvent.duration ?? 1}
                    durationLimitInSeconds={availableTimeForDurations}
                    disabled={!isEditable || runTimer || dataLoading}
                    onDelete={handleDeleteDurationEvent}
                    onUpdate={(duration: number) =>
                      handleUpdateDurationEvent({ ...durationEvent, duration })
                    }
                  />
                </Box>
              ))
            ) : (
              <Typography textAlign="left" mb={1}>
                No events added
              </Typography>
            )}
            {collapseEvents && (
              <Box
                sx={{
                  backgroundColor: FORTA_BLUE.light3,
                  width: 'auto',
                  borderRadius: '4px',
                  textAlign: 'center',
                  py: 1,
                  px: 2,
                  '&:hover': {
                    cursor: 'pointer',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
                  },
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setCollapseEvents(false);
                }}
              >
                <Typography fontWeight={700} sx={{ letterSpacing: '2px' }}>
                  ...
                </Typography>
              </Box>
            )}
          </Grid>
          {isEditable && (
            <Grid
              xs={12}
              sm={12}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 2,
                width: '100%',
              }}
            >
              <FHButton
                startIcon={<AddCircleOutlineOutlined />}
                disabled={!isEditable || runTimer || availableTimeForDurations < 1 || dataLoading}
                onClick={() => {
                  handleAddNewDurationEvent(1);
                  setCollapseEvents(false);
                }}
              >
                Add event
              </FHButton>
            </Grid>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default ParentSNDurationTargetRow;
